import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import styles from "./threatIncidentManagement.module.scss";
import { threatIncidents } from "./threatIncidentData";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";

const statusTone = {
  contained: styles.statusContained,
  investigating: styles.statusInvestigating,
  mitigated: styles.statusMitigated,
  monitoring: styles.statusMonitoring,
  resolved: styles.statusResolved,
};

const severityTone = {
  critical: styles.sevCritical,
  high: styles.sevHigh,
  medium: styles.sevMedium,
};

const responseStatusTone = {
  completed: styles.responseStatusCompleted,
  "in-progress": styles.responseStatusInProgress,
  pending: styles.responseStatusPending,
};

const timelineTone = {
  critical: styles.timelineDotCritical,
  warning: styles.timelineDotWarning,
  success: styles.timelineDotSuccess,
  info: styles.timelineDotInfo,
};

const DetailStatCard = ({ icon, label, value }) => (
  <Card className={styles.detailStatCard}>
    <Stack direction="row" gap={1}>
      {icon}
      <Typography className={styles.detailStatLabel}>{label}</Typography>
    </Stack>
    <Typography className={styles.detailStatValue} fontWeight={700}>
      {value}
    </Typography>
  </Card>
);

const ThreatIncidentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const incident = useMemo(
    () => threatIncidents.find((item) => item.id === id),
    [id]
  );

  if (!incident) {
    return (
      <Box className={styles.page}>
        <Card className={styles.detailCard}>
          <Typography className={styles.detailTitle}>
            Incident Not Found
          </Typography>
          <Typography className={styles.detailCaption}>
            We could not find a threat incident with that ID.
          </Typography>
          <Button
            variant="outlined"
            className={styles.detailBackButton}
            onClick={() => navigate("/threats-incidents")}
            startIcon={<ArrowBackIosNewRoundedIcon fontSize="small" />}
          >
            Back to Incidents
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={styles.page}>
      <Box className={styles.detailTopBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "Threats & Incidents", href: "/threats-incidents" },
            { label: incident.title },
          ]}
        />

        <Button
          variant="outlined"
          className={styles.detailExportButton}
          startIcon={<DownloadOutlinedIcon fontSize="small" />}
        >
          Export Report
        </Button>
      </Box>

      <Card className={styles.detailHeroCard}>
        <Box className={styles.detailHeroIcon}>
          <ErrorOutlineRoundedIcon />
        </Box>
        <Box className={styles.detailHeroBody}>
          <Box className={styles.detailHeroTitleRow}>
            <Typography className={styles.detailHeroTitle}>
              {incident.title}
            </Typography>
            <Chip
              label={incident.severity}
              size="small"
              className={`${styles.severity} ${
                severityTone[incident.severity] || ""
              }`}
            />
          </Box>
          <Typography className={styles.detailHeroDesc}>
            {incident.description}
          </Typography>
          <Box className={styles.detailHeroMeta}>
            <span>
              <CalendarMonthOutlinedIcon fontSize="inherit" />
              Created: {incident.created}
            </span>
            <span>
              <UpdateOutlinedIcon fontSize="inherit" />
              Updated: {incident.updated}
            </span>
            <span>
              <PersonOutlineOutlinedIcon fontSize="inherit" />
              Assignee: {incident.assignee}
            </span>
          </Box>
        </Box>
        <Box
          className={`${styles.detailHeroStatus} ${
            statusTone[incident.status] || ""
          }`}
        >
          <Typography variant="caption">Status</Typography>
          <Typography className={styles.detailHeroStatusValue}>
            {incident.status}
          </Typography>
        </Box>
      </Card>

      <Box className={styles.detailStatGrid}>
        <DetailStatCard
          icon={<TrackChangesOutlinedIcon className={styles.detailStatIcon} />}
          label="Affected Assets"
          value={incident.affectedAssetsCount}
        />
        <DetailStatCard
          icon={<ShieldOutlinedIcon className={styles.detailStatIcon} />}
          label="Indicators (IoC)"
          value={incident.detectionSource}
        />
        <DetailStatCard
          icon={<BugReportOutlinedIcon className={styles.detailStatIcon} />}
          label="MITRE ATT&CK"
          value={incident.mitreCount}
        />
        <DetailStatCard
          icon={<TimelineOutlinedIcon className={styles.detailStatIcon} />}
          label="Timeline Events"
          value={incident.timelineEventsCount}
        />
      </Box>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Impact Assessment
        </Typography>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} sm={6}>
            <Box className={styles.impactItem}>
              <Typography className={styles.impactLabel}>Scope</Typography>
              <Typography className={styles.impactValue}>
                {incident.impact.scope}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={styles.impactItemWarning}>
              <Typography className={styles.impactLabel}>
                Business Impact
              </Typography>
              <Typography className={styles.impactValue}>
                {incident.impact.business}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={styles.impactItemCritical}>
              <Typography className={styles.impactLabel}>
                Severity Assessment
              </Typography>
              <Typography className={styles.impactValue}>
                {incident.impact.severity}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={styles.impactItemSuccess}>
              <Typography className={styles.impactLabel}>
                Data Impact
              </Typography>
              <Typography className={styles.impactValue}>
                {incident.impact.data}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Affected Assets
        </Typography>
        <Box className={styles.assetTable}>
          <div className={styles.assetRowHeader}>
            <span>Asset Name</span>
            <span>Type</span>
            <span>IP Address</span>
            <span>Zone</span>
            <span>Actions</span>
          </div>
          {incident.assets.map((asset) => (
            <div key={asset.name} className={styles.assetRow}>
              <span className={styles.assetName}>{asset.name}</span>
              <span>{asset.type}</span>
              <span className={styles.assetIp}>{asset.ip}</span>
              <span className={styles.assetZone}>{asset.zone}</span>
              <Button
                size="small"
                className={styles.assetAction}
                startIcon={<VisibilityOutlinedIcon fontSize="small" />}
              >
                View
              </Button>
            </div>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Indicators of Compromise (IoC)
        </Typography>
        <Box className={styles.iocList}>
          {incident.iocs.map((ioc) => (
            <Box
              key={`${ioc.label}-${ioc.value}`}
              className={`${styles.iocItem} ${
                styles[`iocItem${ioc.severity}`] || ""
              }`}
            >
              <Box>
                <Typography className={styles.iocLabel}>{ioc.label}</Typography>
                <Typography className={styles.iocValue}>{ioc.value}</Typography>
              </Box>
              <Chip
                size="small"
                label={ioc.severity}
                className={`${styles.iocChip} ${
                  styles[`ioc${ioc.severity}`] || ""
                }`}
              />
            </Box>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          MITRE ATT&CK Techniques
        </Typography>
        <Box className={styles.mitreGrid}>
          {(incident.mitreTechniques || []).map((technique) => (
            <Card key={technique.id} className={styles.mitreCard}>
              <Typography className={styles.mitreCode}>
                {technique.id}
              </Typography>
              <Typography className={styles.mitreName}>
                {technique.name}
              </Typography>
              <Chip
                size="small"
                label={technique.tactic}
                className={styles.mitreTag}
              />
            </Card>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Response Actions
        </Typography>
        <Box className={styles.responseTable}>
          <div className={styles.responseHeaderRow}>
            <span>Action</span>
            <span>Status</span>
            <span>Timestamp</span>
            <span>Result</span>
          </div>
          {(incident.responseActions || []).map((action) => (
            <div
              key={`${action.action}-${action.timestamp}`}
              className={styles.responseRow}
            >
              <span className={styles.responseAction}>{action.action}</span>
              <span
                className={`${styles.responseStatus} ${
                  responseStatusTone[action.status] || ""
                }`}
              >
                {action.status}
              </span>
              <span className={styles.responseTimestamp}>
                {action.timestamp}
              </span>
              <span className={styles.responseResult}>{action.result}</span>
            </div>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Collected Artifacts & Evidence
        </Typography>
        <Grid container spacing={3}>
          {(incident.collectedArtifacts || []).map((artifact) => (
            <Grid key={artifact.name} item sm={6} xs={12}>
              <Card className={styles.artifactCard}>
                <Box className={styles.artifactIcon}>
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                </Box>
                <Box className={styles.artifactBody}>
                  <Typography className={styles.artifactName}>
                    {artifact.name}
                  </Typography>
                  <Typography className={styles.artifactMeta}>
                    {artifact.type} - {artifact.size}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  className={styles.artifactAction}
                  startIcon={<DownloadOutlinedIcon fontSize="small" />}
                >
                  Download
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Security Recommendations
        </Typography>
        <Box className={styles.recommendationList}>
          {(incident.securityRecommendations || []).map((item) => (
            <Box key={item} className={styles.recommendationItem}>
              <CheckCircleOutlinedIcon className={styles.recommendationIcon} />
              <Typography className={styles.recommendationText}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Incident Timeline
        </Typography>
        <Box className={styles.timelineList}>
          {(incident.incidentTimeline || []).map((entry, index) => (
            <Box
              key={`${entry.title}-${entry.timestamp}`}
              className={styles.timelineItem}
            >
              <Box className={styles.timelineMarker}>
                <span
                  className={`${styles.timelineDot} ${
                    timelineTone[entry.tone] || ""
                  }`}
                />
                {index < incident.incidentTimeline.length - 1 && (
                  <span className={styles.timelineLine} />
                )}
              </Box>
              <Box className={styles.timelineContent}>
                <Box className={styles.timelineHeader}>
                  <Typography className={styles.timelineTitle}>
                    {entry.title}
                  </Typography>
                  <Typography className={styles.timelineTimestamp}>
                    {entry.timestamp}
                  </Typography>
                </Box>
                <Typography className={styles.timelineDescription}>
                  {entry.description}
                </Typography>
                <Chip
                  size="small"
                  label={entry.source}
                  className={styles.timelineChip}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Card>

      <Card className={styles.detailSectionCard}>
        <Typography className={styles.detailSectionTitle}>
          Analyst Notes
        </Typography>
        <Box className={styles.noteList}>
          {(incident.analystNotes || []).map((note) => (
            <Box
              key={`${note.author}-${note.timestamp}`}
              className={styles.noteCard}
            >
              <Box className={styles.noteHeader}>
                <AccountCircleOutlinedIcon className={styles.noteIcon} />
                <Typography className={styles.noteAuthor}>
                  {note.author}
                </Typography>
                <span className={styles.noteDot} />
                <Typography className={styles.noteTimestamp}>
                  {note.timestamp}
                </Typography>
              </Box>
              <Typography className={styles.noteText}>{note.note}</Typography>
            </Box>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default ThreatIncidentDetail;
